import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { baseUrlApi } from '../../utils/url';

interface RoomState {
  id: number;
  date: string;
  wall: number;
  floor: number;
  ceiling: number;
  window: number;
  frame: number;
  electricity: number;
  socket: number;
  prise?: number;
  state_type: string;
  description: string;
  RoomId: number;
  LocalId?: number;
}

interface RoomStateState {
  roomstates: RoomState[];
  roomstatesByLocal: RoomState[];
  roomstate: RoomState | null;
  loading: boolean;
  error: string | null;
}

const initialState: RoomStateState = {
  roomstates: [],
  roomstatesByLocal: [],
  roomstate: null,
  loading: false,
  error: null,
};

export const fetchRoomStates = createAsyncThunk(
  'roomstate/fetchRoomStates',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      //const OwnerId = state.authentification.user.OwnerId;
      const token = state.authentification.user.token;
      const response = await axios.get(`${baseUrlApi}/roomstates`, {
        headers: { Authorization: `bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch roomstates');
    }
  }
);

// ✅ NOUVELLE ACTION: Récupérer les roomstates d'un local spécifique
export const fetchRoomStatesByLocal = createAsyncThunk(
  'roomstate/fetchRoomStatesByLocal',
  async (localId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      const response = await axios.get(`${baseUrlApi}/roomstates/local/${localId}`, {
        headers: { Authorization: `bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch roomstates by local');
    }
  }
);

export const fetchRoomState = createAsyncThunk(
  'roomstate/fetchRoomState',
  async (roomstateId: number, { getState, dispatch, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      const response = await axios.get(`${baseUrlApi}/roomstates/${roomstateId}`, {
        headers: { Authorization: `bearer ${token}` }
      });
      dispatch(fetchRoomStates());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch roomstate');
    }
  }
);

export const createRoomState = createAsyncThunk(
  'roomstate/createRoomState',
  async (roomstateData: Partial<RoomState>, { getState, dispatch, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      const response = await axios.post(`${baseUrlApi}/roomstates`, roomstateData, {
        headers: { Authorization: `bearer ${token}` }
      });
      
      // ✅ Recharger les roomstates du local après création
      if (roomstateData.LocalId) {
        dispatch(fetchRoomStatesByLocal(roomstateData.LocalId));
      }
      dispatch(fetchRoomStates());
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create roomstate');
    }
  }
);

export const updateRoomState = createAsyncThunk(
  'roomstate/updateRoomState',
  async ({ roomstateData, roomstateId }: { roomstateData: Partial<RoomState>; roomstateId: number }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      const response = await axios.put(`${baseUrlApi}/roomstates/${roomstateId}`, roomstateData, {
        headers: { Authorization: `bearer ${token}` }
      });
      
      // ✅ Recharger les roomstates du local après mise à jour
      if (roomstateData.LocalId) {
        dispatch(fetchRoomStatesByLocal(roomstateData.LocalId));
      }
      dispatch(fetchRoomStates());
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update roomstate');
    }
  }
);

export const deleteRoomState = createAsyncThunk(
  'roomstate/deleteRoomState',
  async ({ roomstateId, localId }: { roomstateId: number; localId?: number }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.authentification.user.token;
      await axios.delete(`${baseUrlApi}/roomstates/${roomstateId}`, {
        headers: { Authorization: `bearer ${token}` }
      });
      
      // ✅ Recharger les roomstates du local après suppression
      if (localId) {
        dispatch(fetchRoomStatesByLocal(localId));
      }
      dispatch(fetchRoomStates());
      
      return roomstateId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete roomstate');
    }
  }
);

const roomstateSlice = createSlice({
  name: 'roomstate',
  initialState,
  reducers: {
    clearRoomState: (state) => { 
      state.roomstate = null; 
    },
    clearRoomStates: (state) => { 
      state.roomstates = []; 
    },
    clearRoomStatesByLocal: (state) => {
      state.roomstatesByLocal = [];
    },
    resetRoomStateState: () => initialState,
  },
  extraReducers: (builder) => {
    // Pending states
    [fetchRoomStates, fetchRoomStatesByLocal, fetchRoomState, createRoomState, updateRoomState, deleteRoomState].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
        });
    });

    // Fulfilled states
    builder
      .addCase(fetchRoomStates.fulfilled, (state, action) => {
        state.loading = false;
        state.roomstates = action.payload;
      })
      .addCase(fetchRoomStatesByLocal.fulfilled, (state, action) => {
        state.loading = false;
        state.roomstatesByLocal = action.payload;
      })
      .addCase(fetchRoomState.fulfilled, (state, action) => {
        state.loading = false;
        state.roomstate = action.payload;
      })
      .addCase(createRoomState.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateRoomState.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteRoomState.fulfilled, (state, action) => {
        state.loading = false;
        state.roomstates = state.roomstates.filter(item => item.id !== action.payload);
        state.roomstatesByLocal = state.roomstatesByLocal.filter(item => item.id !== action.payload);
      });
  },
});

export const { clearRoomState, clearRoomStates, clearRoomStatesByLocal, resetRoomStateState } = roomstateSlice.actions;
export default roomstateSlice.reducer;